// src/config/features.js
// Feature flags for V2 API migration and other experimental features

/**
 * Feature flags configuration for OriginSelect
 * These flags control the rollout of new features and API migrations
 */
export const FEATURES = {
  // V2 API Migration Flags
  USE_V2_PAGINATION: process.env.REACT_APP_USE_V2_PAGINATION === 'true',
  
  // Individual V2 endpoint flags for granular control
  V2_ENDPOINTS: {
    PRODUCTS: process.env.REACT_APP_V2_PRODUCTS === 'true',
    CATEGORY_COUNTRIES: process.env.REACT_APP_V2_CATEGORY_COUNTRIES === 'true',
    CATEGORY_SUBCATEGORY_COUNTRIES: process.env.REACT_APP_V2_CATEGORY_SUBCATEGORY_COUNTRIES === 'true',
    SEARCH: process.env.REACT_APP_V2_SEARCH === 'true',
    LOVED_PRODUCTS: process.env.REACT_APP_V2_LOVED_PRODUCTS === 'true',
    // Keep legacy flags for backward compatibility
    CATEGORIES: process.env.REACT_APP_V2_CATEGORIES === 'true',
    COUNTRIES: process.env.REACT_APP_V2_COUNTRIES === 'true',
    SUBCATEGORIES: process.env.REACT_APP_V2_SUBCATEGORIES === 'true'
  },
  
  // Pagination-specific settings
  PAGINATION_SETTINGS: {
    DEFAULT_LIMIT: parseInt(process.env.REACT_APP_PAGINATION_LIMIT) || 100,
    MAX_LIMIT: 200, // Server-enforced maximum
    PREFETCH_THRESHOLD: 0.8, // Load next page when 80% through current
    CACHE_PAGES: parseInt(process.env.REACT_APP_CACHE_PAGES) || 5, // Maximum pages to keep in cache
    CACHE_TTL: 5 * 60 * 1000, // 5 minutes cache TTL for paginated data
    
    // Performance thresholds
    MOBILE_LIMIT: 50, // Smaller limit for mobile devices
    SLOW_CONNECTION_LIMIT: 25 // Even smaller for slow connections
  },
  
  // Debug and development flags
  DEBUG: {
    LOG_PAGINATION: process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_PAGINATION === 'true',
    LOG_CACHE_OPERATIONS: process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_CACHE === 'true',
    SHOW_PERFORMANCE_METRICS: process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_PERFORMANCE === 'true'
  },
  
  // Fallback behavior
  FALLBACK: {
    ENABLE_V1_FALLBACK: true, // Always keep V1 as fallback for now
    FALLBACK_TIMEOUT: 5000, // Timeout before falling back to V1 (5 seconds)
    MAX_RETRY_ATTEMPTS: 3 // Number of retries before fallback
  }
};

/**
 * Utility functions for feature flag checking
 */
export const isV2PaginationEnabled = () => FEATURES.USE_V2_PAGINATION;

export const isV2EndpointEnabled = (endpoint) => {
  return FEATURES.V2_ENDPOINTS[endpoint.toUpperCase()] || false;
};

export const getPaginationLimit = () => {
  // Detect mobile devices
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Detect slow connection (if Network Information API is available)
  const isSlowConnection = navigator.connection && 
    (navigator.connection.effectiveType === '2g' || navigator.connection.effectiveType === 'slow-2g');
  
  if (isSlowConnection) {
    return FEATURES.PAGINATION_SETTINGS.SLOW_CONNECTION_LIMIT;
  } else if (isMobile) {
    return FEATURES.PAGINATION_SETTINGS.MOBILE_LIMIT;
  }
  
  return FEATURES.PAGINATION_SETTINGS.DEFAULT_LIMIT;
};

export const shouldUsePrefetch = () => {
  // Disable prefetching on slow connections to save bandwidth
  if (navigator.connection && 
      (navigator.connection.effectiveType === '2g' || navigator.connection.effectiveType === 'slow-2g')) {
    return false;
  }
  
  return true;
};

export const logPaginationDebug = (message, data) => {
  if (FEATURES.DEBUG.LOG_PAGINATION) {
    console.log(`[Pagination Debug] ${message}`, data);
  }
};

export const logCacheDebug = (message, data) => {
  if (FEATURES.DEBUG.LOG_CACHE_OPERATIONS) {
    console.log(`[Cache Debug] ${message}`, data);
  }
};

export const logPerformanceDebug = (message, data) => {
  if (FEATURES.DEBUG.SHOW_PERFORMANCE_METRICS) {
    console.log(`[Performance Debug] ${message}`, data);
  }
};

// Export default for easier importing
export default FEATURES;
